
import { Vue, Component, Prop } from 'vue-property-decorator';
import { getSidoList, getAllSigunguList, Region } from '@/api/region';
import {
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '@/api/notification';
import { storageKey } from '@/enums/localStorage';

@Component({
  name: 'MainHeader',
})
export default class extends Vue {
  @Prop({ default: '' }) private title!: string;

  @Prop({ default: false }) private showNotification!: boolean;

  @Prop({ default: true }) private showBack!: boolean;

  private selectedRegion = ''

  private sidoGroups: Array<{ label: string, code: string, options: Array<{ label: string, value: string }> }> = []

  private showPopover = false;

  private alarmCount = 0;

  private newAlarmCount = 0;

  private alarmList: any = [];

  private alarmList2: any[][] = [];

  private totalElements = 0;

  private totalPages = 0;

  private isLoadingMore = false;

  private hasMore = true;

  private scrollEl: HTMLElement | null = null;

  private listQuery = {
    page: 0,
    size: 5,
  };

  get showRegionSelector(): boolean {
    const hiddenRoutes = ['Match', 'MyPage', 'TeamPage'];
    return !hiddenRoutes.includes(String(this.$route.name));
  }

  mounted() {
    this.loadNotifications();
    this.loadUnreadCount();
    this.loadRegionsAndInitialize();
  }

  private goBack() {
    this.$router.go(-1);
  }

  private handleNotification() {
    this.$emit('notification-click');
  }

  private goToCalendar(): void {
    this.$router.push('/calendar');
  }

  private onRegionChange(value: string): void {
    if (!value) {
      // clearable 없이는 발생하지 않지만, 방어 처리
      this.selectedRegion = this.getDefaultRegionCode();
      localStorage.setItem(storageKey.selectedRegion, this.selectedRegion);
      this.$emit('region-change', this.selectedRegion);
      return;
    }
    localStorage.setItem(storageKey.selectedRegion, value);
    this.$emit('region-change', value);
  }

  private async loadNotifications(reset = true): Promise<void> {
    try {
      const response = await getNotifications({
        page: this.listQuery.page,
        size: this.listQuery.size,
      });
      const pageData = response.data;
      const newItems: any[] = pageData.content || [];
      this.totalElements = pageData.totalElements || 0;
      this.totalPages = pageData.totalPages || 0;
      this.hasMore = this.listQuery.page < this.totalPages - 1;

      if (reset) {
        this.alarmList = newItems;
      } else {
        this.alarmList = [...this.alarmList, ...newItems];
      }

      // 날짜별로 그룹화 (전체 alarmList 기준)
      const itemMap: Map<string, any[]> = new Map();
      this.alarmList.forEach((item: any) => {
        const dateStr: string = item.createdDate
          ? item.createdDate.substring(0, 10)
          : '';
        if (!dateStr) return;
        if (!itemMap.has(dateStr)) {
          itemMap.set(dateStr, []);
        }
        const itemList: any[] | undefined = itemMap.get(dateStr);
        if (itemList) {
          itemList.push({
            id: item.uid,
            title: item.title,
            content: item.content,
            createDate: item.createdDate,
            link: item.actionUrl || '#',
            isRead: item.isRead,
          });
        }
      });
      this.alarmList2 = Array.from(itemMap.values());
    } catch (error) {
      console.error('알림 목록 로드 실패:', error);
    }
  }

  private async loadUnreadCount(): Promise<void> {
    try {
      const response = await getUnreadNotificationCount();
      this.newAlarmCount = response.data?.count || 0;
    } catch (error) {
      console.error('읽지 않은 알림 개수 로드 실패:', error);
    }
  }

  private async onPopoverShow(): Promise<void> {
    this.listQuery.page = 0;
    this.hasMore = true;
    await this.loadNotifications(true);
    this.$nextTick(() => {
      this.scrollEl = document.querySelector('.el-popover.alarm');
      if (this.scrollEl) {
        this.scrollEl.addEventListener('scroll', this.onAlarmScroll);
      }
    });
  }

  private onPopoverHide(): void {
    if (this.scrollEl) {
      this.scrollEl.removeEventListener('scroll', this.onAlarmScroll);
      this.scrollEl = null;
    }
  }

  private onAlarmScroll(): void {
    if (!this.scrollEl || this.isLoadingMore || !this.hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = this.scrollEl;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      this.loadMoreNotifications();
    }
  }

  private async loadMoreNotifications(): Promise<void> {
    if (this.isLoadingMore || !this.hasMore) return;
    this.isLoadingMore = true;
    this.listQuery.page += 1;
    await this.loadNotifications(false);
    this.isLoadingMore = false;
  }

  private async onNotificationClick(item: any): Promise<void> {
    if (!item.isRead) {
      try {
        await markNotificationAsRead(item.id);
        item.isRead = true;
        if (this.newAlarmCount > 0) this.newAlarmCount -= 1;
      } catch (error) {
        console.error('알림 읽음 처리 실패:', error);
      }
    }
  }

  private async markAllRead(): Promise<void> {
    try {
      await markAllNotificationsAsRead();
      this.newAlarmCount = 0;
      this.alarmList2.forEach((group) => {
        group.forEach((item) => { item.isRead = true; });
      });
    } catch (error) {
      console.error('전체 읽음 처리 실패:', error);
    }
  }

  private async loadRegionsAndInitialize(): Promise<void> {
    try {
      const [sidoRes, sigunguRes] = await Promise.all([getSidoList(), getAllSigunguList()]);
      const sidoList: Region[] = sidoRes?.data || [];
      const sigunguList: Region[] = sigunguRes?.data || [];

      // 시/도별로 시/군/구 그루핑
      this.sidoGroups = sidoList.map((sido) => ({
        label: sido.name,
        code: sido.code,
        options: sigunguList
          .filter((sg) => sg.parentCode === sido.code)
          .map((sg) => ({ label: sg.name, value: sg.code })),
      })).filter((g) => g.options.length > 0);
    } catch (error) {
      console.error('Failed to load regions:', error);
    }
    this.initializeSelectedRegion();
  }

  private initializeSelectedRegion(): void {
    const allOptions = this.sidoGroups.flatMap((g) => g.options);
    const savedRegion = localStorage.getItem(storageKey.selectedRegion);

    if (savedRegion) {
      const isValid = allOptions.some((opt) => opt.value === savedRegion);
      if (isValid) {
        this.selectedRegion = savedRegion;
      } else {
        const matchByName = allOptions.find((opt) => opt.label === savedRegion);
        this.selectedRegion = matchByName ? matchByName.value : this.getDefaultRegionCode();
      }
    } else {
      this.selectedRegion = this.getDefaultRegionCode();
    }

    // 항상 하나는 선택된 상태를 보장
    if (!this.selectedRegion && allOptions.length > 0) {
      this.selectedRegion = allOptions[0].value;
    }

    localStorage.setItem(storageKey.selectedRegion, this.selectedRegion);
    this.$emit('region-change', this.selectedRegion);
  }

  private getDefaultRegionCode(): string {
    const allOptions = this.sidoGroups.flatMap((g) => g.options);
    const jinju = allOptions.find((opt) => opt.label === '진주시');
    return jinju ? jinju.value : (allOptions[0]?.value || '');
  }
}
